import React from 'react';
import { formatTimeSince } from '../hooks/useAutoRefresh';

interface LiveIndicatorProps {
  isRefreshing: boolean;
  secondsSinceUpdate: number;
  isPaused?: boolean;
  pauseReason?: string | null;
  showLiveBadge?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Visual indicator for real-time data updates
 * Shows "Live" badge when active, "Paused" when user is working,
 * refresh spinner, and time since last update
 */
const LiveIndicator: React.FC<LiveIndicatorProps> = ({
  isRefreshing,
  secondsSinceUpdate,
  isPaused = false,
  pauseReason = null,
  showLiveBadge = true,
  className = '',
  style = {},
}) => {
  // Determine badge state
  const getBadgeStyle = () => {
    if (isRefreshing) {
      return {
        backgroundColor: '#3b82f6', // Blue - updating
        color: 'white',
      };
    }
    if (isPaused) {
      return {
        backgroundColor: '#f59e0b', // Amber - paused
        color: 'white',
      };
    }
    return {
      backgroundColor: '#10b981', // Green - live
      color: 'white',
    };
  };

  const getBadgeText = () => {
    if (isRefreshing) return 'Updating...';
    if (isPaused) return 'Paused';
    return 'Live';
  };

  return (
    <div
      className={`live-indicator ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '0.8rem',
        color: '#666',
        ...style,
      }}
    >
      {showLiveBadge && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            ...getBadgeStyle(),
          }}
          title={isPaused && pauseReason ? `Paused: ${pauseReason}` : undefined}
        >
          <span
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: 'currentColor',
              animation: isRefreshing || isPaused ? 'none' : 'pulse 2s infinite',
            }}
          />
          {getBadgeText()}
        </span>
      )}

      {isRefreshing ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          <span
            style={{
              width: '12px',
              height: '12px',
              border: '2px solid #e5e7eb',
              borderTopColor: '#3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          Refreshing...
        </span>
      ) : (
        <span style={{ opacity: 0.7 }}>
          {isPaused ? (
            <span title={pauseReason || undefined}>
              Will refresh when idle
            </span>
          ) : (
            <>Updated {formatTimeSince(secondsSinceUpdate)}</>
          )}
        </span>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LiveIndicator;
